import { createPortal } from "react-dom";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Home, FolderKanban, Briefcase, User } from "lucide-react";
import { useAuthSession } from "@/hooks/use-auth-session";
import { useTranslation } from "@/i18n/LanguageContext";
import { cn } from "@/lib/utils";

type Item = {
  id: string;
  label: string;
  icon: typeof Home;
  to: string;
  isActive: (pathname: string, search: string) => boolean;
};

export function MobileBottomNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuthSession();
  const { t } = useTranslation();

  // Hide on auth/MFA flows to keep them focused
  const hiddenRoutes = ["/auth", "/mfa", "/reset-password"];
  if (hiddenRoutes.includes(location.pathname)) return null;

  const portalLink = (tab: string) => (user ? `/portal?tab=${tab}` : "/auth");

  const items: Item[] = [
    {
      id: "home",
      label: t("mobileNav.home"),
      icon: Home,
      to: "/",
      isActive: (p) => p === "/",
    },
    {
      id: "projects",
      label: t("mobileNav.projects"),
      icon: FolderKanban,
      to: portalLink("projects"),
      isActive: (p, s) =>
        p.startsWith("/portal") && (s.includes("tab=projects") || s.includes("tab=dashboard") || s === ""),
    },
    {
      id: "applications",
      label: t("mobileNav.applications"),
      icon: Briefcase,
      to: user ? "/portal?tab=applications" : "/careers",
      isActive: (p, s) =>
        p.startsWith("/careers") || (p.startsWith("/portal") && s.includes("tab=applications")),
    },
    {
      id: "profile",
      label: t("mobileNav.profile"),
      icon: User,
      to: portalLink("profile"),
      isActive: (p, s) => p.startsWith("/portal") && s.includes("tab=profile"),
    },
  ];

  const handleClick = (e: React.MouseEvent, item: Item) => {
    // Force reload of query param when already on /portal so the tab updates
    if (item.to.startsWith("/portal") && location.pathname.startsWith("/portal")) {
      e.preventDefault();
      navigate(item.to, { replace: true });
    }
  };

  // Rendue directement dans <body> : aucun conteneur parent (animation, transformation)
  // ne peut l'entraîner pendant le défilement.
  if (typeof document === "undefined") return null;

  return createPortal(
    <nav
      className="md:hidden fixed bottom-0 left-0 right-0 z-40 border-t border-border/40 bg-background shadow-[0_-8px_24px_-8px_rgba(0,0,0,0.18)]"
      style={{
        paddingBottom: "env(safe-area-inset-bottom)",
        // Calque graphique dédié : évite le tremblement sur iPhone pendant le défilement
        transform: "translate3d(0,0,0)",
        WebkitTransform: "translate3d(0,0,0)",
        backfaceVisibility: "hidden",
      }}
      aria-label="Navigation mobile"
    >
      <ul className="grid grid-cols-4 px-2 pt-2 pb-3">
        {items.map((item) => {
          const active = item.isActive(location.pathname, location.search);
          const Icon = item.icon;
          return (
            <li key={item.id} className="flex">
              <Link
                to={item.to}
                onClick={(e) => handleClick(e, item)}
                className={cn(
                  "group relative flex-1 flex flex-col items-center justify-center gap-1.5 py-1.5 px-1 rounded-xl text-[11px] font-medium transition-all duration-200",
                  active
                    ? "text-primary"
                    : "text-muted-foreground hover:text-foreground active:scale-95"
  )}
                aria-current={active ? "page" : undefined}
              >
                <span
                  className={cn(
                    "flex items-center justify-center w-11 h-7 rounded-full transition-all duration-200",
                    active
                      ? "bg-primary/15 shadow-sm"
                      : "group-hover:bg-muted/60"
  )}
                >
                  <Icon
                    size={20}
                    className={cn("transition-transform duration-200", active && "scale-110")}
                    strokeWidth={active ? 2.4 : 2}
                  />
                </span>
                <span className="truncate max-w-full leading-none tracking-tight">
                  {item.label}
                </span>
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>,
    document.body
  );
}
